import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Upload, FileAudio, CheckCircle, AlertCircle, Loader, Trash2, Download, Clock, Calendar, Edit2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiCall, apiUpload } from '../services/api';
import { transcribeConversationsMockData } from '../data/mockData'; // Import mockup data

const WhisperTranscribe = () => {
  const navigate = useNavigate();
  
  // File upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [transcription, setTranscription] = useState(null);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('');
  const [transcriptionPrompt, setTranscriptionPrompt] = useState('');
  const [responseFormat, setResponseFormat] = useState('json');

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioLevels, setAudioLevels] = useState(Array(12).fill(0)); // For waveform visualization
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Saved conversations
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [expandedConversation, setExpandedConversation] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewingMeeting, setViewingMeeting] = useState(null);
  
  // Add to existing meeting modal
  const [showMeetingSelector, setShowMeetingSelector] = useState(false);
  const [selectedConversationForMeeting, setSelectedConversationForMeeting] = useState(null);
  const [existingMeetings, setExistingMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  
  // Ref for scrolling to saved conversations
  const savedConversationsRef = useRef(null);

  const categories = ['All', 'Business', 'Personal', 'Meetings', 'Interviews', 'Calls', 'Other'];

  const supportedFormats = ['mp3', 'mp4', 'mpeg', 'mpga', 'm4a', 'wav', 'webm'];

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Auto-refresh conversations if any are still processing
  useEffect(() => {
    const hasProcessing = conversations.some(conv => conv.processing);
    
    if (hasProcessing) {
      // Poll every 5 seconds if there are conversations still processing
      const interval = setInterval(() => {
        loadConversations();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [conversations]);

  // Timer for recording
  useEffect(() => {
    if (isRecording) {
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRecording]);

  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      // USING MOCKUP DATA - Don't fetch from API
      console.log('📋 Using mockup conversations data');
      setConversations(transcribeConversationsMockData);
      // const data = await apiCall('/conversations-transcripts/');
      // console.log(`✅ Loaded ${data.conversations?.length || 0} conversations`);
      // setConversations(data.conversations || []);
    } catch (err) {
      console.error('❌ Error loading conversations:', err);
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Create audio context for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioSource = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      audioSource.connect(analyser);
      analyserRef.current = analyser;

      // Start visualizing audio levels
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateWaveform = () => {
        if (!analyserRef.current) return;
        
        analyser.getByteFrequencyData(dataArray);
        
        // Sample 12 points across the frequency data for our 12 bars
        const bars = [];
        const samplesPerBar = Math.floor(bufferLength / 12);
        
        for (let i = 0; i < 12; i++) {
          const start = i * samplesPerBar;
          const end = start + samplesPerBar;
          let sum = 0;
          for (let j = start; j < end; j++) {
            sum += dataArray[j];
          }
          const average = sum / samplesPerBar;
          // Normalize to 0-100 range
          bars.push(Math.min(100, (average / 255) * 100));
        }
        
        setAudioLevels(bars);
        animationFrameRef.current = requestAnimationFrame(updateWaveform);
      };

      updateWaveform();

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        
        // Stop visualization
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (audioContext) {
          audioContext.close();
        }
        analyserRef.current = null;
        setAudioLevels(Array(12).fill(0)); // Reset bars
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      setError(null);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to access microphone. Please grant permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeRecording = async () => {
    if (!audioBlob) return;

    setTranscribing(true);
    setError(null);

    try {
      // Step 1: Transcribe audio
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('response_format', 'json');

      // Use apiUpload for FormData
      const transcriptData = await apiUpload('/whisper/transcribe', formData);

      // Step 3: Save conversation instantly with quick_save mode. Audio is
      // intentionally omitted — base64-encoded webm exceeded production's
      // ingress body-size limit, causing a 500.
      try {
        const savedConversation = await apiCall('/conversations-transcripts/?quick_save=true', {
          method: 'POST',
          body: JSON.stringify({
            transcript: transcriptData.text,
            duration: recordingTime,
            language: transcriptData.language
          })
        });

        console.log('✅ Conversation saved:', savedConversation);
        
        // Reload conversations to show the new one
        await loadConversations();

        // Reset states after everything is done
        setAudioBlob(null);
        setRecordingTime(0);
        setTranscribing(false);

        // Scroll to saved conversations section to show the new conversation
        setTimeout(() => {
          if (savedConversationsRef.current) {
            savedConversationsRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 300);
      } catch (saveErr) {
        console.error('❌ Save failed:', saveErr);
        setError(saveErr.message || 'Failed to save conversation');
        setTranscribing(false);
      }

    } catch (err) {
      console.error('❌ Error processing recording:', err);
      setError(err.message || 'Failed to process recording');
      setTranscribing(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    try {
      await apiCall(`/conversations-transcripts/${conversationId}`, {
        method: 'DELETE'
      });
      await loadConversations();
      alert('✅ Conversation deleted successfully');
    } catch (err) {
      console.error('Delete error:', err);
      alert('❌ Error deleting conversation');
    }
  };

  const downloadTranscriptFile = (conversation) => {
    const content = `${conversation.title}\n${'='.repeat(50)}\n\nDate: ${new Date(conversation.created_at).toLocaleString()}\nDuration: ${Math.floor(conversation.duration)}s\n\n--- TRANSCRIPT ---\n${conversation.transcript}\n\n--- AI SUMMARY ---\n${conversation.ai_summary || conversation.summary}`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${conversation.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleNewMeeting = (conversation) => {
    // Navigate to Meetings page with transcript pre-filled
    const convDate = new Date(conversation.created_at);
    const defaultDate = convDate.toISOString().split('T')[0];
    const defaultTime = convDate.toTimeString().split(' ')[0].substring(0, 5);

    const prefillData = {
      title: conversation.title,
      date: defaultDate,
      time: defaultTime,
      duration: Math.ceil(conversation.duration / 60), // Convert seconds to minutes
      transcript: conversation.transcript, // Put transcript in transcript field
      notes: conversation.ai_summary || conversation.summary || '', // Put AI summary in notes field
      transcription_id: conversation.id, // Store the conversation ID for linking
      participants: []
    };

    // Navigate to meetings page with prefill data
    navigate('/meetings', {
      state: { prefillData }
    });
  };

  const openMeetingSelector = async (conversation) => {
    setSelectedConversationForMeeting(conversation);
    setShowMeetingSelector(true);
    setLoadingMeetings(true);
    
    try {
      const meetings = await apiCall('/meetings/');
      setExistingMeetings(meetings);
    } catch (error) {
      console.error('Error loading meetings:', error);
      alert('Failed to load meetings');
    } finally {
      setLoadingMeetings(false);
    }
  };

  const addTranscriptToExistingMeeting = async (meetingId) => {
    try {
      // Get the meeting details first
      const meeting = await apiCall(`/meetings/${meetingId}`);
      
      // Append the transcript
      const updatedTranscript = meeting.transcript 
        ? `${meeting.transcript}\n\n--- Added on ${new Date().toLocaleString()} ---\n${selectedConversationForMeeting.transcript}`
        : selectedConversationForMeeting.transcript;
      
      // Update the meeting
      await apiCall(`/meetings/${meetingId}`, {
        method: 'PUT',
        body: JSON.stringify({
          transcript: updatedTranscript
        })
      });

      // Update the conversation to link it to the meeting
      await apiCall(`/conversations-transcripts/${selectedConversationForMeeting.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          meeting_id: meetingId
        })
      });

      // Reload conversations to show the link
      await loadConversations();
      
      // Close modal
      setShowMeetingSelector(false);
      setSelectedConversationForMeeting(null);
      
      alert('✅ Transcript added to meeting successfully!');
    } catch (error) {
      console.error('Error adding transcript to meeting:', error);
      alert('❌ Failed to add transcript to meeting');
    }
  };

  const viewMeetingFromConversation = async (meetingId) => {
    try {
      const meeting = await apiCall(`/meetings/${meetingId}`);
      setViewingMeeting(meeting);
    } catch (error) {
      console.error('Error fetching meeting:', error);
      alert('Failed to load meeting details');
    }
  };

  const renameSpeaker = async (conversationId, oldName, newName) => {
    if (!newName || newName.trim() === '') return;
    
    try {
      await apiCall(`/conversations-transcripts/${conversationId}/rename-speaker`, {
        method: 'PATCH',
        body: JSON.stringify({
          old_name: oldName,
          new_name: newName.trim()
        })
      });
      await loadConversations();
      alert(`✅ Renamed "${oldName}" to "${newName}"`);
    } catch (err) {
      console.error('Error renaming speaker:', err);
      alert('Failed to rename speaker');
    }
  };

  const updateConversationCategory = async (conversationId, newCategory) => {
    try {
      await apiCall(`/conversations-transcripts/${conversationId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          category: newCategory
        })
      });
      
      // Update local state
      setConversations(conversations.map(conv =>
        conv.id === conversationId ? { ...conv, category: newCategory } : conv
      ));
      
      if (expandedConversation && expandedConversation.id === conversationId) {
        setExpandedConversation({ ...expandedConversation, category: newCategory });
      }
    } catch (err) {
      console.error('Error updating category:', err);
      alert('Failed to update category');
    }
  };

  const renameConversation = async (conversationId, newTitle) => {
    if (!newTitle || newTitle.trim() === '') return;
    
    try {
      await apiCall(`/conversations-transcripts/${conversationId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: newTitle.trim()
        })
      });
      await loadConversations();
      if (expandedConversation && expandedConversation.id === conversationId) {
        setExpandedConversation({ ...expandedConversation, title: newTitle.trim() });
      }
      alert('✅ Title updated');
    } catch (err) {
      console.error('Error renaming conversation:', err);
      alert('Failed to update title');
    }
  };

  const parseTranscriptToMessages = (transcript) => {
    // Split by speaker pattern (e.g., "Darren:", "Person 2:")
    const lines = transcript.split('\n').filter(line => line.trim());
    const messages = [];
    
    lines.forEach(line => {
      const match = line.match(/^(.+?):\s*(.+)$/);
      if (match) {
        messages.push({
          speaker: match[1].trim(),
          text: match[2].trim()
        });
      } else if (messages.length > 0) {
        // Continue previous message
        messages[messages.length - 1].text += ' ' + line.trim();
      }
    });
    
    return messages;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (25MB max)
    if (file.size > 100 * 1024 * 1024) {
      setError('File size exceeds 100MB limit');
      return;
    }

    // Validate file format
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!supportedFormats.includes(fileExt)) {
      setError(`Unsupported format. Please use: ${supportedFormats.join(', ')}`);
      return;
    }

    setSelectedFile(file);
    setError(null);
    setTranscription(null);
  };

  const handleTranscribe = async () => {
    if (!selectedFile) {
      setError('Please select an audio file');
      return;
    }

    setTranscribing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('audio', selectedFile);
      if (language) formData.append('language', language);
      if (transcriptionPrompt) formData.append('prompt', transcriptionPrompt);
      formData.append('response_format', responseFormat);

      // Use apiUpload for FormData
      const data = await apiUpload('/whisper/transcribe', formData);
      setTranscription(data);
    } catch (err) {
      console.error('Transcription error:', err);
      setError(err.message || 'Failed to transcribe audio');
    } finally {
      setTranscribing(false);
    }
  };

  const downloadTranscript = () => {
    if (!transcription) return;

    const blob = new Blob([transcription.text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${transcription.id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <Mic className="text-cyan-500" size={40} />
            Live Transcription System
          </h1>
          <p className="text-gray-400">
            Record conversations, meetings, and calls - Auto-transcribed with AI summaries
          </p>
        </div>

        {/* Live Recording Section */}
        <div className="border bg-gradient-to-br from-red-500/30 via-pink-600/20 to-purple-500/30 border-2 border-red-500/40 rounded-2xl shadow-2xl shadow-red-500/10 p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Mic size={24} />
            Live Recording
          </h2>

          {!isRecording && !audioBlob && (
            <div className="text-center py-8">
              <Button
                onClick={startRecording}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 rounded-full text-lg font-semibold shadow-lg hover:shadow-red-500/50 transition-all"
              >
                <Mic size={28} className="mr-2" />
                Start Recording
              </Button>
              <p className="text-gray-400 mt-4 text-sm">
                Click to start recording your conversation
              </p>
            </div>
          )}

          {isRecording && (
            <div className="text-center py-8">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-2xl font-bold text-white">{formatTime(recordingTime)}</span>
              </div>
              
              {/* Vocal Waveform Visualization */}
              <div className="flex items-center justify-center gap-1 h-24 mb-6">
                {audioLevels.map((level, idx) => (
                  <div
                    key={idx}
                    className="w-3 bg-gradient-to-t from-gray-500 to-purple-500 rounded-full transition-all duration-100"
                    style={{
                      height: `${Math.max(8, level)}%`,
                      opacity: 0.7 + (level / 100) * 0.3
                    }}
                  />
                ))}
              </div>
              
              <p className="text-gray-300 mb-6">Recording in progress...</p>
              <Button
                onClick={stopRecording}
                className="bg-black hover:bg-white/5 text-white px-8 py-4 rounded-lg text-lg font-semibold"
              >
                Stop Recording
              </Button>
            </div>
          )}

          {audioBlob && !transcribing && (
            <div className="text-center py-8">
              <CheckCircle className="text-green-500 mx-auto mb-4" size={48} />
              <p className="text-white text-lg mb-2">Recording Complete!</p>
              <p className="text-gray-400 mb-6">Duration: {formatTime(recordingTime)}</p>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={transcribeRecording}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 rounded-lg"
                >
                  Transcribe & Save
                </Button>
                <Button
                  onClick={() => {
                    setAudioBlob(null);
                    setRecordingTime(0);
                  }}
                  className="bg-black hover:bg-white/5 text-white px-6 py-3 rounded-lg"
                >
                  Discard
                </Button>
              </div>
            </div>
          )}

          {transcribing && (
            <div className="text-center py-8">
              <Loader className="animate-spin text-cyan-500 mx-auto mb-4" size={48} />
              <p className="text-white text-lg font-semibold">Saving your recording...</p>
              <p className="text-gray-400 text-sm mt-2">Transcribing and saving (AI summary will process in background)</p>
            </div>
          )}
        </div>

        {/* Upload Section */}
        <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Upload size={24} />
            Upload Audio File
          </h2>

          {/* File Input */}
          <div className="mb-6">
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-white/30 rounded-lg cursor-pointer hover:bg-white/5 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <FileAudio className="text-cyan-500 mb-3" size={48} />
                <p className="mb-2 text-sm text-gray-300">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  {supportedFormats.map(f => f.toUpperCase()).join(', ')} (Max 100MB)
                </p>
                {selectedFile && (
                  <p className="mt-2 text-sm text-cyan-400 font-medium">
                    ✓ {selectedFile.name}
                  </p>
                )}
              </div>
              <input
                type="file"
                className="hidden"
                accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                onChange={handleFileSelect}
              />
            </label>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Language (Optional)
              </label>
              <input
                type="text"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="e.g., en, es, fr"
                className="w-full bg-white/10 border border-white/60 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
              />
              <p className="text-xs text-gray-500 mt-1">ISO 639-1 code (en, es, fr, de, etc.)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Response Format
              </label>
              <select
                value={responseFormat}
                onChange={(e) => setResponseFormat(e.target.value)}
                className="w-full bg-white/10 border border-white/60 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-white"
              >
                <option value="json">JSON (default)</option>
                <option value="text">Plain Text</option>
                <option value="verbose_json">Verbose JSON (with timestamps)</option>
                <option value="srt">SRT (subtitles)</option>
                <option value="vtt">VTT (subtitles)</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Prompt (Optional)
            </label>
            <textarea
              value={transcriptionPrompt}
              onChange={(e) => setTranscriptionPrompt(e.target.value)}
              placeholder="Add context to guide the transcription style..."
              rows={2}
              className="w-full bg-white/10 border border-white/60 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white resize-none"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-300">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Transcribe Button */}
          <Button
            onClick={handleTranscribe}
            disabled={!selectedFile || transcribing}
            className={`w-full py-6 text-lg font-semibold ${
              !selectedFile || transcribing
                ? 'bg-black cursor-not-allowed'
                : 'bg-gradient-to-r from-gray-600 to-gray-600 hover:from-gray-700 hover:to-gray-700'
            } text-white rounded-lg transition-all`}
          >
            {transcribing ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="animate-spin" size={24} />
                Transcribing...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Mic size={24} />
                Transcribe Audio
              </span>
            )}
          </Button>
        </div>

        {/* Transcription Result */}
        {transcription && (
          <div className="border bg-gradient-to-br from-green-500/20 via-emerald-600/10 to-teal-500/20 border-2 border-green-500/40 rounded-2xl shadow-2xl shadow-green-500/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <CheckCircle className="text-green-500" size={24} />
                Transcription Complete
              </h2>
              <Button
                onClick={downloadTranscript}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
              >
                Download
              </Button>
            </div>

            <div className="mb-4 text-sm text-gray-400">
              <p>
                <span className="font-medium">File:</span> {transcription.title}
              </p>
              {transcription.language && (
                <p>
                  <span className="font-medium">Language:</span> {transcription.language}
                </p>
              )}
              {transcription.duration && (
                <p>
                  <span className="font-medium">Duration:</span> {formatTime(Math.floor(transcription.duration))}
                </p>
              )}
            </div>

            <div className="bg-white/10 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="text-white whitespace-pre-wrap text-sm leading-relaxed">
                {transcription.text}
              </pre>
            </div>

            {/* Segments (if verbose_json) */}
            {transcription.segments && transcription.segments.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-white mb-2">Segments</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {transcription.segments.map((segment, idx) => (
                    <div key={idx} className="bg-white/10 rounded p-3 text-sm">
                      <div className="text-gray-400 mb-1">
                        {segment.start.toFixed(2)}s - {segment.end.toFixed(2)}s
                      </div>
                      <div className="text-white">{segment.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Saved Conversations */}
        <div className="mt-8" ref={savedConversationsRef}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <FileAudio size={28} />
            Saved Conversations ({conversations.filter(c => selectedCategory === 'all' || (c.category || 'Other').toLowerCase() === selectedCategory.toLowerCase()).length})
          </h2>

          {/* Category Tabs */}
          <div className="mb-4">
            <div className="flex gap-2 flex-wrap">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category === 'All' ? 'all' : category)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    selectedCategory === (category === 'All' ? 'all' : category).toLowerCase()
                      ? 'bg-cyan-600 text-white'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {loadingConversations ? (
            <div className="text-center py-8">
              <Loader className="animate-spin text-cyan-500 mx-auto" size={32} />
            </div>
          ) : conversations.filter(c => selectedCategory === 'all' || (c.category || 'Other').toLowerCase() === selectedCategory.toLowerCase()).length === 0 ? (
            <div className="border bg-white/5 border border-white/10 rounded-xl p-8 text-center">
              <FileAudio className="text-gray-500 mx-auto mb-3" size={48} />
              <p className="text-gray-400">
                {selectedCategory === 'all' 
                  ? 'No conversations recorded yet'
                  : `No ${selectedCategory} conversations yet`}
              </p>
              <p className="text-gray-500 text-sm mt-2">Start recording to create your first transcription</p>
            </div>
          ) : (
            <div className="overflow-y-auto grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ maxHeight: '520px' }}>
              {conversations.filter(c => selectedCategory === 'all' || (c.category || 'Other').toLowerCase() === selectedCategory.toLowerCase()).map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => setExpandedConversation(conv)}
                  className="border bg-gradient-to-br from-gray-500/20 via-purple-600/10 to-pink-500/20 border-2 border-white/30 rounded-xl shadow-lg p-5 hover:border-blue-400/50 transition-all cursor-pointer"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newTitle = prompt('Edit conversation title:', conv.title);
                          if (newTitle && newTitle !== conv.title) {
                            renameConversation(conv.id, newTitle);
                          }
                        }}
                        className="text-lg font-semibold text-white text-left hover:text-cyan-400 transition-colors flex items-center gap-2 group"
                        title="Click to edit title"
                      >
                        {conv.title}
                        <Edit2 size={16} className="opacity-0 group-hover:opacity-100 transition-opacity text-cyan-400" />
                      </button>
                      {/* Category Badge with Edit */}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-3 py-1 bg-purple-600/30 text-purple-300 rounded-full text-xs">
                          {conv.category || 'Other'}
                        </span>
                        <select
                          value={conv.category || 'Other'}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateConversationCategory(conv.id, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs bg-transparent text-gray-300 border border-white/60 rounded px-2 py-1 cursor-pointer hover:border-cyan-500"
                        >
                          {categories.filter(c => c !== 'All').map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNewMeeting(conv);
                        }}
                        className="px-3 py-1 bg-green-500/30 hover:bg-green-500/50 text-green-300 rounded-lg text-xs flex items-center gap-1 transition-colors"
                        title="Create new meeting from this transcription"
                      >
                        <Calendar size={14} />
                        <span>New Meeting</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openMeetingSelector(conv);
                        }}
                        className="px-3 py-1 bg-white/30 hover:bg-white/50 text-gray-200 rounded-lg text-xs flex items-center gap-1 transition-colors"
                        title="Add transcript to existing meeting"
                      >
                        <Calendar size={14} />
                        <span>Add to Meeting</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadTranscriptFile(conv);
                        }}
                        className="text-cyan-400 hover:text-cyan-300 p-1"
                        title="Download"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conv.id);
                        }}
                        className="text-red-400 hover:text-red-300 p-1"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(conv.created_at).toLocaleDateString()} {new Date(conv.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {formatTime(Math.floor(conv.duration))}
                    </span>
                  </div>

                  {/* Transcript Preview */}
                  {conv.transcript && (
                    <div className="mb-3 p-3 bg-white/5 border border-white/10 rounded-lg">
                      <p className="text-sm text-gray-300 line-clamp-3">
                        {conv.transcript.length > 200 
                          ? conv.transcript.substring(0, 200) + '...' 
                          : conv.transcript}
                      </p>
                    </div>
                  )}

                  {/* Meeting Link Indicator */}
                  {conv.meeting_id && (
                    <div className="mb-3 p-2 bg-green-500/20 border border-green-500/30 rounded-lg">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          viewMeetingFromConversation(conv.meeting_id);
                        }}
                        className="flex items-center gap-2 text-green-400 hover:text-green-300 text-sm"
                      >
                        <CheckCircle size={16} />
                        <span>Meeting Created - Click to View</span>
                      </button>
                    </div>
                  )}

                  {/* AI Processing Indicator */}
                  {conv.processing && (
                    <div className="mb-3 p-2 bg-white/20 border border-cyan-500/30 rounded-lg">
                      <div className="flex items-center gap-2 text-cyan-400 text-sm">
                        <Loader size={16} className="animate-spin" />
                        <span>AI summary generating in background...</span>
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-cyan-400 mb-2">AI Summary:</h4>
                    <div className="bg-transparent rounded-lg p-3 text-sm text-gray-300 max-h-32 overflow-y-auto">
                      <pre className="whitespace-pre-wrap font-sans">{conv.ai_summary || conv.summary}</pre>
                    </div>
                  </div>

                  {/* Transcript as Chat */}
                  <div>
                    <h4 className="text-sm font-semibold text-purple-400 mb-2">Conversation:</h4>
                    <div className="bg-transparent rounded-lg p-3 max-h-64 overflow-y-auto space-y-2">
                      {parseTranscriptToMessages(conv.transcript).map((msg, idx) => (
                        <div key={idx} className="text-sm">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const newName = prompt(`Rename "${msg.speaker}" to:`, msg.speaker);
                              if (newName && newName !== msg.speaker) {
                                renameSpeaker(conv.id, msg.speaker, newName);
                              }
                            }}
                            className={`font-semibold hover:underline cursor-pointer ${
                              msg.speaker === 'Darren' ? 'text-cyan-400' : 'text-orange-400'
                            }`}
                          >
                            {msg.speaker}:
                          </button>
                          <span className="text-gray-300 ml-2">{msg.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Expanded Conversation Modal */}
        {expandedConversation && (
          <div className="fixed inset-0 bg-transparent z-50 flex items-center justify-center p-4" onClick={() => setExpandedConversation(null)}>
            <div className="bg-gradient-to-br from-[#1a1f3e] to-[#0a0f2e] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border-2 border-cyan-500/50" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-gradient-to-r from-gray-600 to-gray-600 p-6 flex items-start justify-between">
                <div className="flex-1">
                  <button
                    onClick={() => {
                      const newTitle = prompt('Edit conversation title:', expandedConversation.title);
                      if (newTitle && newTitle !== expandedConversation.title) {
                        renameConversation(expandedConversation.id, newTitle);
                      }
                    }}
                    className="text-2xl font-bold text-white mb-2 hover:text-gray-200 transition-colors flex items-center gap-2 group"
                  >
                    {expandedConversation.title}
                    <Edit2 size={20} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <div className="flex items-center gap-4 text-sm text-gray-200">
                    <span className="flex items-center gap-1">
                      <Calendar size={16} />
                      {new Date(expandedConversation.created_at).toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={16} />
                      {formatTime(Math.floor(expandedConversation.duration))}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedConversation(null)}
                  className="text-white hover:text-gray-300 p-2"
                >
                  <AlertCircle size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {/* AI Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center gap-2">
                    <CheckCircle size={20} />
                    AI Summary
                  </h3>
                  <div className="bg-transparent rounded-lg p-4 border border-cyan-500/30">
                    <pre className="text-gray-300 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                      {expandedConversation.ai_summary || expandedConversation.summary}
                    </pre>
                  </div>
                </div>

                {/* Full Conversation */}
                <div>
                  <h3 className="text-lg font-semibold text-purple-400 mb-3 flex items-center gap-2">
                    <Mic size={20} />
                    Full Conversation
                  </h3>
                  <div className="bg-transparent rounded-lg p-4 border border-purple-500/30 space-y-3">
                    {parseTranscriptToMessages(expandedConversation.transcript).map((msg, idx) => (
                      <div key={idx} className="flex gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newName = prompt(`Rename "${msg.speaker}" to:`, msg.speaker);
                            if (newName && newName !== msg.speaker) {
                              renameSpeaker(expandedConversation.id, msg.speaker, newName);
                              setExpandedConversation(null);
                            }
                          }}
                          className={`font-semibold text-sm px-3 py-1 rounded-full whitespace-nowrap hover:opacity-80 ${
                            msg.speaker === 'Darren' 
                              ? 'bg-cyan-600 text-white' 
                              : 'bg-orange-600 text-white'
                          }`}
                          title="Click to rename speaker"
                        >
                          {msg.speaker}
                        </button>
                        <p className="text-gray-300 flex-1 text-sm leading-relaxed">{msg.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex gap-3 justify-end">
                  <Button
                    onClick={() => downloadTranscriptFile(expandedConversation)}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2"
                  >
                    <Download size={18} className="mr-2" />
                    Download
                  </Button>
                  <Button
                    onClick={() => {
                      if (window.confirm('Delete this conversation?')) {
                        deleteConversation(expandedConversation.id);
                        setExpandedConversation(null);
                      }
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2"
                  >
                    <Trash2 size={18} className="mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Meeting Details Modal */}
        {viewingMeeting && (
          <div className="fixed inset-0 bg-transparent backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-black border-2 border-white/60 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-cyan-400">Meeting Details</h2>
                <button
                  onClick={() => setViewingMeeting(null)}
                  className="text-gray-400 hover:text-white p-2"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Title</label>
                  <div className="text-white text-lg font-semibold">{viewingMeeting.title}</div>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Date</label>
                    <div className="text-white">{viewingMeeting.date}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Time</label>
                    <div className="text-white">{viewingMeeting.time}</div>
                  </div>
                </div>

                {/* Participants */}
                {viewingMeeting.participants && viewingMeeting.participants.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Participants</label>
                    <div className="space-y-2">
                      {viewingMeeting.participants.map((p, idx) => (
                        <div key={idx} className="text-white text-sm">
                          {p.name} {p.email && `(${p.email})`}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {viewingMeeting.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Notes</label>
                    <div className="text-gray-300 whitespace-pre-wrap bg-transparent rounded-lg p-3">
                      {viewingMeeting.notes}
                    </div>
                  </div>
                )}

                {/* Transcript */}
                {viewingMeeting.transcript && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Transcript</label>
                    <div className="text-gray-300 whitespace-pre-wrap bg-transparent rounded-lg p-3 font-mono text-sm max-h-64 overflow-y-auto">
                      {viewingMeeting.transcript}
                    </div>
                  </div>
                )}

                {/* View Full Meeting Button */}
                <div className="pt-4 border-t border-white/60">
                  <button
                    onClick={() => {
                      navigate('/meetings');
                      setViewingMeeting(null);
                    }}
                    className="w-full py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
                  >
                    Go to Meetings Page
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Meeting Selector Modal */}
        {showMeetingSelector && (
          <div className="fixed inset-0 bg-transparent backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-black border-2 border-white/60 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-gray-600 to-gray-600 p-6 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Select Meeting</h2>
                <button
                  onClick={() => {
                    setShowMeetingSelector(false);
                    setSelectedConversationForMeeting(null);
                  }}
                  className="text-white hover:text-gray-300 p-2"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                {loadingMeetings ? (
                  <div className="text-center py-8">
                    <Loader className="animate-spin text-cyan-500 mx-auto" size={32} />
                    <p className="text-gray-400 mt-4">Loading meetings...</p>
                  </div>
                ) : existingMeetings.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="text-gray-500 mx-auto mb-3" size={48} />
                    <p className="text-gray-400">No meetings found</p>
                    <p className="text-gray-500 text-sm mt-2">Create a meeting first to add transcripts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {existingMeetings.map((meeting) => (
                      <div
                        key={meeting.id}
                        onClick={() => addTranscriptToExistingMeeting(meeting.id)}
                        className="bg-gradient-to-br from-gray-500/20 via-purple-600/10 to-pink-500/20 border-2 border-white/30 rounded-xl p-4 hover:border-blue-400/50 transition-all cursor-pointer"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-lg font-semibold text-white">{meeting.title}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            meeting.status === 'upcoming'
                              ? 'bg-green-500/30 text-green-300'
                              : meeting.status === 'done'
                              ? 'bg-white/30 text-gray-200'
                              : 'bg-transparent text-gray-300'
                          }`}>
                            {meeting.status}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {meeting.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {meeting.time}
                          </span>
                          {meeting.duration && (
                            <span>{meeting.duration} min</span>
                          )}
                        </div>

                        {meeting.participants && meeting.participants.length > 0 && (
                          <div className="mt-2 text-sm text-gray-400">
                            <span className="font-medium">Participants:</span> {meeting.participants.map(p => p.name).join(', ')}
                          </div>
                        )}

                        {meeting.transcript && (
                          <div className="mt-2 px-2 py-1 bg-white/20 border border-cyan-500/30 rounded text-xs text-cyan-300">
                            ✓ Has existing transcript
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-white/60 bg-transparent">
                <p className="text-sm text-gray-400 text-center">
                  Click on a meeting to add the transcript
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhisperTranscribe;
